/**
 * Per-project task states API client (Phase 19).
 *
 * Wraps Phase 18's `GET /api/projects/:projectId/states` (API-02).
 * Consumed by `useProjectStates(projectId)` (`frontend/src/hooks/useProjectStates.ts`)
 * which feeds:
 *   - the inline status chip popover on `TaskManagementPage.tsx` (plan 19-05)
 *   - the per-project filter chip-row above each project's table (plan 19-07)
 *
 * Type mirrors backend `statePublicShape` from
 * `backend/src/api/schemas/taskState.ts` (Phase 18, frozen).
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';

// ===== Types =====

export interface TaskState {
  id: number;
  projectId: number;
  name: string;
  ordering: number;
  color: string; // '#RRGGBB'
  // 260723-tst — terminal (no-longer-in-progress) flag. Editable in the project
  // Task States list + on the board column header; suppresses the age 💩 emoji.
  // Server always sends it; optional on the wire type only so legacy test
  // fixtures need not set it.
  isTerminal?: boolean;
  // 260726-wip — "work in progress" flag. Editable in the project Task States
  // list + on the board column header; mutually exclusive with isTerminal.
  // Server always sends it; optional on the wire type only so legacy test
  // fixtures need not set it.
  isInProgress?: boolean;
  /**
   * MKS-154 — raw task_states.sla_timer_effect ('running' | 'response_stopped'
   * | 'resolved' | 'waiting' | 'terminal'). Server always sends it (a real
   * row always has the NOT NULL DEFAULT value); optional on the wire type
   * only so legacy test fixtures need not set it.
   */
  slaTimerEffect?: string;
  /**
   * MKS-154 — true iff this state's slaTimerEffect matches what the backend
   * terminal-no-return guards treat as locked. Server always sends it;
   * optional on the wire type only so legacy test fixtures need not set it.
   */
  isLocked?: boolean;
  createdAt: string;
  updatedAt: string;
}

// ===== API Functions =====

/**
 * Fetch the ordered state list for a single project.
 *
 * Returned states are sorted by `ordering ASC` (server side — Phase 18
 * taskStateService.listStates). Each entry's `color` is a 7-char hex
 * suitable for inline `style={{ backgroundColor }}` and the
 * `chipTextColor()` helper.
 */
export async function getProjectStates(projectId: number): Promise<TaskState[]> {
  const res = await apiGet<{ data: TaskState[] }>(`/projects/${projectId}/states`);
  return res.data;
}

// ===== Phase 20 additions: per-project state CRUD + reorder + re-apply =====

/**
 * Body for POST /api/projects/:projectId/states (Phase 18 API-02; D-23).
 *
 * The Phase 20 docstring originally claimed the server picks `ordering`, but
 * the backend Zod schema (`createStateSchema` in
 * `backend/src/api/schemas/taskState.ts:28`) requires it from the body — the
 * old docstring was wrong. Until that backend contract is relaxed, callers
 * MUST pass `ordering` (zero-based index where the new state will land in the
 * project's existing list — typically the current state count, so the new
 * column appends to the end).
 *
 * User UAT 2026-06-10 surfaced this on the board's `+ Add state` (400 Bad
 * Request); the same gap also affected ProjectFormPage's add path.
 */
export interface CreateProjectStateInput {
  name: string;
  ordering: number;
  color: string; // '#RRGGBB'
  isTerminal?: boolean; // 260723-tst — defaults to false server-side
  isInProgress?: boolean; // 260726-wip — defaults to false server-side
}

/**
 * Body for PATCH /api/projects/:projectId/states/:stateId (Phase 18 API-02).
 * Either or both of `name` / `color` may be sent.
 */
export interface UpdateProjectStateInput {
  name?: string;
  color?: string;
  isTerminal?: boolean; // 260723-tst
  isInProgress?: boolean; // 260726-wip — mutually exclusive with isTerminal
}

/**
 * Single-conflict shape returned in 409 STATE_IN_USE responses
 * (Phase 18 D-03). The frontend reads this from
 * `ApiClientError.details.conflicts[*]` to surface task counts and
 * deep-link `View tasks` actions.
 */
export interface ReapplyConflict {
  stateId: number;
  stateName: string;
  taskCount: number;
  taskIds: number[];
  taskIdsTruncated?: boolean;
}

/**
 * Narrow helper for callers that catch errors from
 * `deleteProjectState` / `reapplyProjectTemplate` and need to read
 * the conflict shape. Use:
 *   if (isStateInUseError(err)) { err.details.conflicts.forEach(...) }
 */
export interface StateInUseError {
  conflicts: ReapplyConflict[];
}

export function isStateInUseError(
  err: unknown
): err is { details: StateInUseError; statusCode: 409 } {
  if (!err || typeof err !== 'object') return false;
  const e = err as { statusCode?: number; details?: unknown };
  if (e.statusCode !== 409) return false;
  const d = e.details as { conflicts?: unknown } | undefined;
  return Array.isArray(d?.conflicts);
}

/**
 * Server-side count summary returned by the re-apply endpoint
 * (`reapplyResponseSchema` in `backend/src/api/schemas/projectTemplate.ts`
 * lines 164–171). NOT a TaskState[]. The ReapplyTemplateModal does not
 * consume this value — it only triggers cache invalidation and surfaces
 * a success toast.
 */
export interface ReapplyResult {
  added: number;
  updated: number;
  removed: number;
}

/** Admin/PM: append a new state to the project's list. */
export async function createProjectState(
  projectId: number,
  input: CreateProjectStateInput
): Promise<TaskState> {
  const res = await apiPost<{ data: TaskState }>(`/projects/${projectId}/states`, input);
  return res.data;
}

/** Admin/PM: rename / recolor a state. */
export async function updateProjectState(
  projectId: number,
  stateId: number,
  patch: UpdateProjectStateInput
): Promise<TaskState> {
  const res = await apiPatch<{ data: TaskState }>(
    `/projects/${projectId}/states/${stateId}`,
    patch
  );
  return res.data;
}

/**
 * Admin/PM: soft-delete a state. Returns 204 on success.
 * On 409 STATE_IN_USE the thrown ApiClientError carries
 * `details: { conflicts: ReapplyConflict[] }` (length 1 — Phase 18 D-03).
 * UI surface: AppToast deep-link to TaskManagementPage filter chip (D-20).
 */
export async function deleteProjectState(projectId: number, stateId: number): Promise<void> {
  await apiDelete<void>(`/projects/${projectId}/states/${stateId}`);
}

/**
 * Admin/PM: persist the new ordering after a drag-drop drop event.
 * Body shape is positional — `stateIds` array IS the new order.
 * (Phase 18 reorder body shape; CONTEXT.md D-13 — drag-and-drop fires onReorder.)
 */
export async function reorderProjectStates(
  projectId: number,
  stateIds: number[]
): Promise<TaskState[]> {
  const res = await apiPatch<{ data: TaskState[] }>(
    `/projects/${projectId}/states/reorder`,
    { stateIds }
  );
  return res.data;
}

/**
 * Admin-only: replace the project's state list with a template's snapshot
 * (PROJ-04, Phase 18 API-05, D-18). Returns a count summary
 * `{ added, updated, removed }` — NOT a TaskState[]. The caller
 * (ReapplyTemplateModal) does not read the value; it just triggers cache
 * invalidation and surfaces a success toast. On 409 STATE_IN_USE the
 * thrown ApiClientError carries `details: { conflicts: ReapplyConflict[] }`
 * (length = removed-with-tasks count) — caller keeps the modal open and
 * renders inline conflict badges per D-19.
 */
export async function reapplyProjectTemplate(
  projectId: number,
  projectTemplateId: number
): Promise<ReapplyResult> {
  const res = await apiPost<{ data: ReapplyResult }>(
    `/admin/projects/${projectId}/reapply-template`,
    { projectTemplateId }
  );
  return res.data;
}
